import Base2DElement from "./base-2d-element";
import GraphContainer from "./graphing/graph_container";
import GraphGrid from "./graphing/graph_grid";
import RenderManager from "../rendering/render-manager";
import { fire } from "../utils/events";
import DataPoint from "./graphing/graph_data/data_point";
import DataGraph from "./graphing/graph_data/data_graph";
import DataArbitrary from "./graphing/graph_data/data_arbitrary";
import SystemDefaults from "../system-defaults";
import getLetterbox from "../helpers/get-letterbox";

class Graphing extends Base2DElement {
    get type2d() { return "Graphing"; }
    _initValues(options) {
        this._showAxisTitles = "style" in options && "showAxisTitles" in options.style ? options.style.showAxisTitles : this._showAxisTitles;
        this._showAxisNumbers = "style" in options && "showAxisNumbers" in options.style ? options.style.showAxisTitles : this._showAxisNumbers;
        this._showAxisX = "style" in options && "showAxisX" in options.style ? options.style.showAxisX : this._showAxisX;
        this._showAxisY = "style" in options && "showAxisY" in options.style ? options.style.showAxisY : this._showAxisY;
        this._showAxisArrowX = "style" in options && "showAxisArrowX" in options.style ? options.style.showAxisArrowX : this._showAxisArrowX;
        this._showAxisArrowY = "style" in options && "showAxisArrowY" in options.style ? options.style.showAxisArrowY : this._showAxisArrowY;
        this._axisWidth = "style" in options && "axisWidth" in options.style ? options.style.axisWidth : this._axisWidth;
        this._showGrid = "style" in options && "showGrid" in options.style ? options.style.showGrid : this._showGrid;
        this._colorBackground = "style" in options && "colorBackground" in options.style ? options.style.colorBackground : this._colorBackground;
        this._colorAxes = "style" in options && "colorAxes" in options.style ? options.style.colorAxes : this._colorAxes;
        this._colorMajor = "style" in options && "colorMajor" in options.style ? options.style.colorMajor : this._colorMajor;
        this._colorMinor = "style" in options && "colorMinor" in options.style ? options.style.colorMinor : this._colorMinor;
        this._colorText = "style" in options && "colorText" in options.style ? options.style.colorText : this._colorText;
        this._colorTextShadow = "style" in options && "colorTextShadow" in options.style ? options.style.colorTextShadow : this._colorTextShadow;
        this._viewerInteractable = "viewerInteractable" in options ? options.viewerInteractable : this._viewerInteractable;
        this._xLabel = "style" in options && "xLabel" in options.style ? options.style.xLabel : this._xLabel;
        this._yLabel = "style" in options && "yLabel" in options.style ? options.style.yLabel : this._yLabel;
        this._gridType = "style" in options && "gridType" in options.style ? options.style.gridType : this._gridType;

        this._grid = new GraphGrid({ parent: this, viewerInteractable: Q3.editMode || this._viewerInteractable });

        if ("range" in options) {
            const ranges = this._grid.getRangeRangesViewport();

            if ("locked" in options.range) ranges.sizeLg.locked = options.range.locked;
            if ("x" in options.range) ranges.sizeLg.x.fromArray(options.range.x);
            if ("y" in options.range) ranges.sizeLg.y.fromArray(options.range.y);

            if ("sizeSm" in options.range) {
                if ("locked" in options.range.sizeSm) ranges.sizeSm.locked = options.range.sizeSm.locked;
                if ("x" in options.range.sizeSm) ranges.sizeSm.x.fromArray(options.range.sizeSm.x);
                if ("y" in options.range.sizeSm) ranges.sizeSm.y.fromArray(options.range.sizeSm.y);
            }
        }

        if ("datapoints" in options) this._grid.data = options.datapoints.map(data => {
            if (data.isDataObject) return data;
            switch (data.type) {
                case "point":
                    return DataPoint.deserialize(this, data);
                case "graph":
                    return DataGraph.deserialize(this, data);
                case "arbitrary":
                    return DataArbitrary.deserialize(this, data);
            }
        });
    }

    setData(data) {
        const currentSize = this.getVWSize();
        const size = {
            x: data.x, y: data.y,
            width: data.width, height: data.height
        };

        Object.assign(this[currentSize], size);

        this.data[currentSize] = {};

        Object.assign(this.data[currentSize], size);

        this._xy.set(size.x, size.y);
        this._wh.set(size.width, size.height);

        this.needsUpdate = true;
    }

    _initDefaultVars() {
        super._initDefaultVars();

        this._wh.set(1, 1);

        const defaults = SystemDefaults.defaultGraphingSettings();

        this._gridType = defaults.gridType;
        this._showAxisTitles = defaults.showAxisTitles;
        this._showAxisNumbers = defaults.showAxisNumbers;
        this._showAxisX = defaults.showAxisX;
        this._showAxisY = defaults.showAxisY;
        this._showAxisArrowX = defaults.showAxisArrowX;
        this._showAxisArrowY = defaults.showAxisArrowY;
        this._axisWidth = defaults.axisWidth;
        this._showGrid = defaults.showGrid;
        this._colorBackground = defaults.colorBackground;
        this._colorAxes = defaults.colorAxes;
        this._colorText = defaults.colorText;
        this._colorTextShadow = defaults.colorTextShadow;
        this._colorMajor = defaults.colorMajor;
        this._colorMinor = defaults.colorMinor;
        this._viewerInteractable = defaults.viewerInteractable;
        this._xLabel = "";
        this._yLabel = "";
    }

    get charts() { return this._grid.charts; }

    toJSON() {
        const ranges = this._grid.getRangeRangesViewport();

        return {
            range: {
                x: ranges.sizeLg.x.toArray(),
                y: ranges.sizeLg.y.toArray(),
                locked: ranges.sizeLg.locked,
                sizeSm: {
                    x: ranges.sizeSm.x.toArray(),
                    y: ranges.sizeSm.y.toArray(),
                    locked: ranges.sizeSm.locked,
                }
            },
            datapoints: this._grid.data,
            style: {
                showAxisTitles: this._showAxisTitles,
                showAxisNumbers: this._showAxisNumbers,
                showAxisX: this._showAxisX,
                showAxisY: this._showAxisY,
                gridType: this._gridType,
                showAxisArrowX: this._showAxisArrowX,
                showAxisArrowY: this._showAxisArrowY,
                axisWidth: this._axisWidth,
                showGrid: this._showGrid,
                colorBackground: this._colorBackground,
                colorAxes: this._colorAxes,
                colorMajor: this._colorMajor,
                colorMinor: this._colorMinor,
                colorText: this._colorText,
                colorTextShadow: this._colorTextShadow,
                xLabel: this._xLabel,
                yLabel: this._yLabel
            },
            viewerInteractable: this._viewerInteractable
        };
    }

    get xLabel() { return this._xLabel; }
    set xLabel(v) { this._xLabel = v; }
    get yLabel() { return this._yLabel; }
    set yLabel(v) { this._yLabel = v; }

    get viewerInteractable() { return this._viewerInteractable; }
    set viewerInteractable(v) { this._viewerInteractable = v; }

    get locked() { return this._grid.getRangeRangesViewport()[this.getVWSize()].locked; }
    set locked(val) { this._grid.getRangeRangesViewport()[this.getVWSize()].locked = val; }

    get xRangeMin() { return this._grid.getRangeRangesViewport()[this.getVWSize()].x.x; }
    set xRangeMin(val) { this._grid.getRangeRangesViewport()[this.getVWSize()].x.x = val; }

    get xRangeMax() { return this._grid.getRangeRangesViewport()[this.getVWSize()].x.y; }
    set xRangeMax(val) { this._grid.getRangeRangesViewport()[this.getVWSize()].x.y = val; }

    get yRangeMin() { return this._grid.getRangeRangesViewport()[this.getVWSize()].y.x; }
    set yRangeMin(val) { this._grid.getRangeRangesViewport()[this.getVWSize()].y.x = val; }

    get yRangeMax() { return this._grid.getRangeRangesViewport()[this.getVWSize()].y.y; }
    set yRangeMax(val) { this._grid.getRangeRangesViewport()[this.getVWSize()].y.y = val; }

    get disableContext() { return this.editing; }

    get editState() { return this._grid.editState; }

    get colorBackground() { return this._colorBackground; }
    set colorBackground(v) { this._colorBackground = v; }

    get colorText() { return this._colorText; }
    set colorText(v) { this._colorText = v; }

    get colorTextShadow() { return this._colorTextShadow; }
    set colorTextShadow(v) { this._colorTextShadow = v; }

    get colorAxes() { return this._colorAxes; }
    set colorAxes(v) { this._colorAxes = v; }

    get colorMajor() { return this._colorMajor; }
    set colorMajor(v) { this._colorMajor = v; }

    get colorMinor() { return this._colorMinor; }
    set colorMinor(v) { this._colorMinor = v; }

    get axisWidth() { return this._axisWidth; }
    set axisWidth(v) { this._axisWidth = v; }

    get showAxisArrowX() { return this._showAxisArrowX; }
    set showAxisArrowX(v) { this._showAxisArrowX = v; }

    get showAxisArrowY() { return this._showAxisArrowY; }
    set showAxisArrowY(v) { this._showAxisArrowY = v; }

    get gridType() { return this._gridType; }
    set gridType(v) { this._gridType = v; }

    get showAxisTitles() { return this._showAxisTitles; }
    set showAxisTitles(v) { this._showAxisTitles = v; }

    get showAxisNumbers() { return this._showAxisNumbers; }
    set showAxisNumbers(v) { this._showAxisNumbers = v; }

    get showAxisX() { return this._showAxisX; }
    set showAxisX(v) { this._showAxisX = v; }

    get showAxisY() { return this._showAxisY; }
    set showAxisY(v) { this._showAxisY = v; }

    get showGrid() { return this._showGrid; }
    set showGrid(v) { this._showGrid = v; }

    _createContainer() {
        const container = document.createElementNS(Base2DElement.Namespace, "g");

        if (!Q3.editMode) {
            if (this.hiddenMobile) { container.classList.add("hidden-mobile"); }
            if (this.hiddenDesktop) { container.classList.add("hidden-desktop"); }
        }

        return container;
    }

    constructor(options, parentLayer, uuid) {
        super(options, parentLayer, uuid);

        this._container = new GraphContainer(this.domElement);

        if (Q3.editMode) {
            this.domElement.addEventListener("click", e => {
                this.selectHandler(e);
            }, false);
        }

        this.domElement.style.pointerEvents = "auto";
        this.domElement.classList.add("graphing");
    }

    getRenderPositionAndSize(width, height) {
        const pr = RenderManager.pixelRatio;
        const size = this.getVWSize();

        if (this[size]) { Object.assign(this, this[size]); }

        if (!width) { width = size.width; }
        if (!height) { height = size.height; }

        if (!Q3.editMode) {
            const letterbox = getLetterbox();

            width = parseFloat(letterbox.w) / pr;
            height = parseFloat(letterbox.h) / pr;
        }

        let w = this.width * width * pr;
        let h = this.height * height * pr;

        if (w < 40) { w = 40; }
        if (h < 40) { h = 40; }

        const x = this.x * width * pr;
        const y = this.y * height * pr;

        return { x, y, w, h };
    }

    updateEditState() { this._grid.updateEditState(); }

    set editing(v) {
        if (v === false) {
            this._grid.stopEditing();
        } else throw new Error("Can only cancel editing.");
    }

    get editing() {
        return this._grid.isEditing || this._grid.isPanning;
    }

    //y range: -2.819672131147541, 2.819672131147541
    redraw(width, height) {
        if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return;

        const { x, y, w, h } = this.getRenderPositionAndSize(width, height);

        const container = this._container;
        const grid = this._grid;

        grid
            .axisWidth(this._axisWidth)
            .showAxisArrowX(this._showAxisArrowX)
            .showAxisArrowY(this._showAxisArrowY)
            .showAxisTitles(this._showAxisTitles)
            .showAxisNumbers(this._showAxisNumbers)
            .showAxisX(this._showAxisX)
            .showAxisY(this._showAxisY)
            .gridType(this._gridType)
            .showGrid(this._showGrid)
            .colorBackground(this._colorBackground)
            .colorAxes(this._colorAxes)
            .colorMajor(this._colorMajor)
            .colorMinor(this._colorMinor)
            .colorText(this._colorText)
            .colorTextShadow(this._colorTextShadow)
            .xLabel(this._xLabel)
            .yLabel(this._yLabel)
            .setSize({ x, y, width: w, height: h })
            .redraw();

        container.erase();
        container.append(grid);
    }

    delete() {
        this.parentLayer.objects.splice(this.parentLayer.objects.indexOf(this), 1);

        delete this.domElement;

        if (Q3.selected === this) {
            Q3.selected = null;
            fire("selectchange");
        }
    }

    createObjectFromJSON(data) {
        let el;
        switch (data.type) {
            case "point": el = DataPoint.deserialize(this, data); break;
            case "graph": el = DataGraph.deserialize(this, data); break;
            case "arbitrary": el = DataArbitrary.deserialize(this, data); break;
        }
        this.charts.data().push(el);
        return el;
    }

    createObject(type) {
        let el = null;
        switch (type) {
            case "point": el = new DataPoint(this); break;
            case "graph": el = new DataGraph(this); break;
            case "arbitrary": el = new DataArbitrary(this); break;
            default: return null;
        }

        this.charts.data().push(el);

        return el;
    }
}

export default Graphing;
export { Graphing }
